import type { PrinterMeterCounter, PrinterMeterDay } from '../../api/assets-api'

export type PrinterMetersSearch = { from?: string; to?: string; format?: string }
export type MeterSort = { counterKey: string | null; direction: 'asc' | 'desc' }

const DAY = /^\d{4}-\d{2}-\d{2}$/
const PAGE_COUNT_FORMAT = /^[A-Za-z][A-Za-z0-9_]{0,63}$/
const DAY_MS = 86_400_000
const DEFAULT_DAYS = 31
const RETAINED_MONTHS = 13

export function decodePrinterMetersSearch(search: Record<string, unknown>): PrinterMetersSearch {
  const decoded: PrinterMetersSearch = {}
  if (isDay(search.from)) decoded.from = search.from
  if (isDay(search.to)) decoded.to = search.to
  if (typeof search.format === 'string' && PAGE_COUNT_FORMAT.test(search.format)) decoded.format = search.format
  return decoded
}

export function meterWindow(search: PrinterMetersSearch, now = new Date()): { from: string; to: string } {
  const to = search.to ?? utcDay(now)
  const from = search.from ?? utcDay(new Date(dayStart(to) - (DEFAULT_DAYS - 1) * DAY_MS))
  const [earlier, later] = from <= to ? [from, to] : [to, from]
  const earliest = monthsBefore(later, RETAINED_MONTHS)
  return { from: earlier < earliest ? earliest : earlier, to: later }
}

export function sortMeterDays(
  days: readonly PrinterMeterDay[], counters: readonly PrinterMeterCounter[], sort: MeterSort,
): PrinterMeterDay[] {
  const sign = sort.direction === 'asc' ? 1 : -1
  const column = counters.findIndex((counter) => counter.key === sort.counterKey)
  return [...days].sort((left, right) => {
    if (column === -1) return sign * left.day.localeCompare(right.day)
    const a = left.values[column] ?? null
    const b = right.values[column] ?? null
    if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1
    return sign * (a - b) || right.day.localeCompare(left.day)
  })
}

function monthsBefore(day: string, months: number) {
  const target = new Date(dayStart(day))
  const date = target.getUTCDate()
  target.setUTCDate(1)
  target.setUTCMonth(target.getUTCMonth() - months + 1, 0)
  target.setUTCDate(Math.min(date, target.getUTCDate()))
  return utcDay(target)
}

function isDay(value: unknown): value is string {
  if (typeof value !== 'string' || !DAY.test(value)) return false
  const start = dayStart(value)
  return Number.isFinite(start) && utcDay(new Date(start)) === value
}

function dayStart(day: string) {
  return Date.parse(`${day}T00:00:00Z`)
}

function utcDay(date: Date) {
  return date.toISOString().slice(0, 10)
}
